// The small, pure pieces of the Boot screens: sizes, the health thresholds, the licence's grace warning, the preparation
// report's steps, and the patches a realtime frame makes to a list. No I/O, so each is tested on its own.

import type { BootDisk, BootImage, BootImageVersion, BootMetricPoint, BootPc, BootServer, BootVersionProgress } from '@clavian/api-client';

// -- Sizes ---------------------------------------------------------------------------------------

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;

/** A byte count as staff read it: "312 GB", "2.8 TB". Binary units, one decimal under 10, none above. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const text = value >= 10 || unit === 0 ? String(Math.round(value)) : (Math.round(value * 10) / 10).toString();
  return `${text} ${UNITS[unit]}`;
}

/** The share of a disk in use, 0 to 100, whole percent. An empty disk reads 0. */
export function usedPercent(disk: Pick<BootDisk, 'totalBytes' | 'freeBytes'>): number {
  if (disk.totalBytes <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round(((disk.totalBytes - disk.freeBytes) / disk.totalBytes) * 100)));
}

/** A drive is running out of room: under one tenth free. */
export function isDiskLow(disk: Pick<BootDisk, 'totalBytes' | 'freeBytes'>): boolean {
  return disk.totalBytes > 0 && disk.freeBytes * 10 < disk.totalBytes;
}

// -- Health thresholds (a value turns amber past these) ---------------------------------------------

/** 99th-percentile read latency above 5 ms, in microseconds. */
export const P99_AMBER_US = 5_000;
/** Cache hits below 80 %. */
export const HIT_AMBER_PCT = 80;
/** The server's uplink above 85 % used. */
export const UPLINK_AMBER_PCT = 85;

export const isSlowP99 = (us: number): boolean => us > P99_AMBER_US;
export const isLowHit = (pct: number): boolean => pct < HIT_AMBER_PCT;
export const isHotUplink = (pct: number): boolean => pct > UPLINK_AMBER_PCT;

/** Microseconds as milliseconds, one decimal: 1800 → "1.8 ms", 7400 → "7.4 ms". Integer arithmetic. */
export function formatMillis(us: number): string {
  const tenths = Math.round(us / 100);
  return `${Math.floor(tenths / 10)}.${tenths % 10} ms`;
}

/** A percentage with no more than one decimal: 94 → "94%", 71.5 → "71.5%". */
export function formatPercent(pct: number): string {
  return `${Math.round(pct * 10) / 10}%`;
}

export interface PcHealth {
  readonly bootPcId: string;
  /** The worst 99th-percentile latency in the range, in microseconds. */
  readonly p99Us: number;
  /** Cache hits over the range, the reads-weighted mean, 0 to 100. */
  readonly hitPct: number;
  readonly points: number;
}

/** One summary per PC from a metrics response: the worst p99, and the hit rate weighted by reads. */
export function summarisePcs(points: readonly BootMetricPoint[]): PcHealth[] {
  const byPc = new Map<string, { p99: number; hits: number; reads: number; n: number }>();
  for (const point of points) {
    if (point.bootPcId === null) continue;
    const current = byPc.get(point.bootPcId) ?? { p99: 0, hits: 0, reads: 0, n: 0 };
    current.p99 = Math.max(current.p99, point.p99Us);
    current.hits += point.hitPct * point.readOps;
    current.reads += point.readOps;
    current.n += 1;
    byPc.set(point.bootPcId, current);
  }
  return [...byPc.entries()].map(([bootPcId, one]) => ({ bootPcId, p99Us: one.p99, hitPct: one.reads === 0 ? 0 : one.hits / one.reads, points: one.n }));
}

/** The server's own row of a metrics response, oldest first. */
export const serverPoints = (points: readonly BootMetricPoint[]): BootMetricPoint[] => points.filter((point) => point.bootPcId === null);

// -- Licence -------------------------------------------------------------------------------------

/** Grace and expiry are warnings: a PC the server does not know yet will not boot in grace, and only the free allowance boots after. */
export const needsLicenceAttention = (server: Pick<BootServer, 'licenceState'>): boolean => server.licenceState === 'grace' || server.licenceState === 'expired';

// -- Versions ------------------------------------------------------------------------------------

export const sortNewestFirst = (versions: readonly BootImageVersion[]): BootImageVersion[] => [...versions].sort((a, b) => b.number - a.number);

/** Versions that can be restored to: published, and not the one in use. */
export function restorePoints(versions: readonly BootImageVersion[], image: Pick<BootImage, 'currentVersionId'>): BootImageVersion[] {
  return sortNewestFirst(versions).filter((version) => version.state === 'published' && version.id !== image.currentVersionId);
}

/**
 * What a merge up to a version takes away: every published version older than it. The chosen version stays, as the new
 * base. The console lists these before it asks the boot server to do it.
 */
export function mergeRemoves(versions: readonly BootImageVersion[], upTo: Pick<BootImageVersion, 'number'>): BootImageVersion[] {
  return sortNewestFirst(versions).filter((version) => version.state === 'published' && version.number < upTo.number);
}

/** A version being made right now: a capture, a preparation or an edit. */
export const isInProgress = (version: Pick<BootImageVersion, 'state'>): boolean =>
  version.state === 'capturing' || version.state === 'preparing' || version.state === 'editing';

// -- The preparation report ------------------------------------------------------------------------

export interface ReportStep {
  readonly name: string;
  readonly status: 'ok' | 'failed' | 'skipped' | 'unknown';
  /** The step's message and notes (why it failed, what it decided), in the server's words. */
  readonly details: readonly string[];
  readonly changes: readonly string[];
}

const STEP_STATUS: Readonly<Record<string, ReportStep['status']>> = {
  ok: 'ok',
  passed: 'ok',
  failed: 'failed',
  skipped: 'skipped',
  notRun: 'skipped',
};

/** One change the server recorded: a string, or `{ kind, target, name, action, before, after }`. */
function changeText(change: unknown): string | undefined {
  if (typeof change === 'string') return asText(change);
  if (typeof change !== 'object' || change === null) return undefined;
  const c = change as Record<string, unknown>;
  const target = asText(c['target']);
  if (target === undefined) return undefined;
  const name = asText(c['name']);
  const after = asText(c['after']);
  const action = asText(c['action']);
  return `${action === undefined ? '' : `${action}: `}${target}${name === undefined ? '' : ` \\ ${name}`}${after === undefined ? '' : ` = ${after}`}`;
}

const asText = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() !== '' ? value : undefined);

/**
 * The steps of a preparation report (`{ steps: [{ name, status, changes }] }`), read leniently: the boot server writes the
 * report, and a step it words differently still shows its name. A report with no steps reads as none.
 */
export function reportSteps(report: Record<string, unknown> | null | undefined): ReportStep[] {
  const steps = report?.['steps'];
  if (!Array.isArray(steps)) return [];
  return steps.flatMap((raw: unknown, index): ReportStep[] => {
    if (typeof raw !== 'object' || raw === null) return [];
    const step = raw as Record<string, unknown>;
    const status = typeof step['status'] === 'string' ? STEP_STATUS[step['status']] : undefined;
    const changes = Array.isArray(step['changes']) ? step['changes'].flatMap((change): string[] => { const text = changeText(change); return text === undefined ? [] : [text]; }) : [];
    const omitted = typeof step['omittedChanges'] === 'number' && step['omittedChanges'] > 0 ? step['omittedChanges'] : 0;
    const notes = Array.isArray(step['notes']) ? step['notes'].flatMap((note): string[] => { const text = asText(note); return text === undefined ? [] : [text]; }) : [];
    const message = asText(step['message']);
    return [
      {
        name: asText(step['title']) ?? asText(step['name']) ?? asText(step['key']) ?? asText(step['id']) ?? `Step ${index + 1}`,
        status: status ?? 'unknown',
        details: [...(message === undefined ? [] : [message]), ...notes],
        changes: omitted > 0 ? [...changes, `+${omitted} more`] : changes,
      },
    ];
  });
}

// -- Realtime patches ---------------------------------------------------------------------------------

/** A boot PC row changed: replace it in the list, or add it; a removed PC leaves the list. */
export function applyPcChanged(list: readonly BootPc[] | undefined, pc: BootPc): BootPc[] | undefined {
  if (list === undefined) return undefined;
  const without = list.filter((one) => one.id !== pc.id);
  return pc.removedAt === null ? [...without, pc].sort((a, b) => a.mac.localeCompare(b.mac)) : without;
}

/** The boot server's row changed: replace it (a venue has one). */
export function applyServerChanged(list: readonly BootServer[] | undefined, server: BootServer): BootServer[] | undefined {
  if (list === undefined) return undefined;
  return list.some((one) => one.id === server.id) ? list.map((one) => (one.id === server.id ? server : one)) : [...list, server];
}

/** The newest piece of a job's state, per version, from the frames seen so far. */
export interface JobProgress {
  readonly state: 'running' | 'succeeded' | 'failed';
  readonly percent: number | null;
  readonly message: string | null;
  readonly sizeBytes: number | null;
  /** A running capture's bytes received and expected; null when the job does not count them. */
  readonly bytesDone: number | null;
  readonly bytesTotal: number | null;
  readonly preparationReport: Record<string, unknown> | null;
  readonly job: string;
  /** The image the job is for, when the boot server named it. */
  readonly imageId?: string | null;
  readonly at: string;
}

// -- A version being made ------------------------------------------------------------------------------

/** How far the job making a version has got: the saved progress, or a newer live frame. */
export interface ProgressNow {
  readonly job: string;
  readonly percent: number | null;
  readonly message: string | null;
  readonly bytesDone: number | null;
  readonly bytesTotal: number | null;
  readonly at: string;
}

/**
 * The newer of a version's saved progress (read with the list, so it survives a refresh) and the latest running frame the
 * live socket brought. A finished frame is not progress: the list is read again and says what happened.
 */
export function progressNow(saved: BootVersionProgress | null | undefined, live: JobProgress | undefined): ProgressNow | undefined {
  const running = live?.state === 'running' ? live : undefined;
  if (running !== undefined && (saved == null || Date.parse(running.at) >= Date.parse(saved.updatedAt))) {
    return { job: running.job, percent: running.percent, message: running.message, bytesDone: running.bytesDone, bytesTotal: running.bytesTotal, at: running.at };
  }
  if (saved == null) return undefined;
  return { job: saved.job, percent: saved.percent, message: saved.message, bytesDone: saved.bytesDone, bytesTotal: saved.bytesTotal, at: saved.updatedAt };
}

/**
 * Bytes done of bytes expected, both in the total's unit with one decimal: 16.3e9 of 43e9 → "15.2" of "40.1" "GB". The
 * same binary units as `formatBytes`. Undefined when there is nothing to count.
 */
export function bytesOf(done: number | null, total: number | null): { readonly done: string; readonly total: string; readonly unit: string } | undefined {
  if (done === null || total === null || !Number.isFinite(done) || !Number.isFinite(total) || total <= 0) return undefined;
  let unit = 0;
  while (total >= 1024 ** (unit + 1) && unit < UNITS.length - 1) unit += 1;
  const scale = 1024 ** unit;
  const text = (bytes: number) => (unit === 0 ? String(Math.round(bytes)) : (Math.round((bytes / scale) * 10) / 10).toFixed(1));
  return { done: text(Math.min(Math.max(0, done), total)), total: text(total), unit: UNITS[unit] ?? 'B' };
}

/** What the Images list's State column says about a version being made. */
export interface ProgressStatus {
  /** Which line of copy names it (`boot.images.status.*`). */
  readonly label: 'waitingForCapture' | 'capturing' | 'importing' | 'preparingJob';
  readonly percent: number | null;
  readonly bytes: ReturnType<typeof bytesOf>;
}

/**
 * A version being made, as the State column words it: the job and its percent, and a capture's bytes. A capture with no
 * progress yet is waiting for the reference PC to start sending.
 */
export function progressStatus(state: BootImageVersion['state'], now: ProgressNow | undefined): ProgressStatus {
  if (now === undefined) return { label: state === 'capturing' ? 'waitingForCapture' : 'preparingJob', percent: null, bytes: undefined };
  const label = now.job === 'capture' ? 'capturing' : now.job === 'import' ? 'importing' : 'preparingJob';
  return { label, percent: now.percent, bytes: bytesOf(now.bytesDone, now.bytesTotal) };
}

/** Every letter a game disk may have, `D` to `Z` (never `A`–`C`). */
export const GAME_DISK_LETTERS: readonly string[] = Array.from({ length: 23 }, (_, i) => String.fromCharCode('D'.charCodeAt(0) + i));

/**
 * The letters a game disk may move to: every one from `D` to `Z` that no other live game disk of the venue holds. Its own
 * letter stays in the list, so the choice starts on it.
 */
export function freeDriveLetters(images: readonly Pick<BootImage, 'id' | 'kind' | 'driveLetter' | 'archivedAt'>[], imageId: string): string[] {
  const held = new Set(images.filter((one) => one.id !== imageId && one.kind === 'game' && one.archivedAt === null && one.driveLetter !== null).map((one) => one.driveLetter));
  return GAME_DISK_LETTERS.filter((letter) => !held.has(letter));
}
